import { Link } from "react-router-dom";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { Reveal, SectionHead } from "@/components/Reveal";
import { FaqAccordion } from "@/components/FaqAccordion";
import { BUSINESS, PAGES } from "@/lib/site";
import { LicenseSection } from "@/components/License";
import { track } from "@/lib/analytics";

const CRUMBS = [{ name: "Home", path: "/" }, { name: "Medical Card", path: "/medical-card" }];

const STEPS = [
  ["Open the ABCA portal", "Tap the button for DC residents or visitors to open the official DC government site (myabca.dc.gov)."],
  ["Create an account", "Sign up with your email and have a valid government-issued photo ID ready."],
  ["Submit your registration", "Confirm you're 21+ and complete the self-certification form on the ABCA site."],
  ["Come upstairs & shop", "Order from our live menu or visit us at 1738 14th St NW, next to Chicken + Whiskey."],
];

const CARD_FAQS = [
  { q: "Do I need to register before buying?", a: "Yes. District Gaslight is a licensed medical cannabis retailer, so every purchase requires a valid medical cannabis patient registration and a 21+ photo ID." },
  { q: "Do I need a doctor's visit?", a: "No. Adults 21+ can self-certify online through the official ABCA portal at myabca.dc.gov." },
  { q: "I'm visiting from out of state — can I still buy?", a: "Yes. Visitors can register through ABCA's non-DC resident form, or bring a valid medical cannabis card from their home state." },
  { q: "Is there a fee or waiting time?", a: "Fees and processing are handled entirely by the DC government through the ABCA portal — please check myabca.dc.gov for current details. We recommend registering before you plan to order." },
  { q: "What should I bring to pick up?", a: "Bring the valid government-issued photo ID you registered with, plus your registration. Then come up the stairs next to Chicken + Whiskey and ring the bell." },
];

const CtaPair = ({ where, dark }) => (
  <div className="flex flex-wrap gap-3">
    <a href={BUSINESS.dcCardUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("medical_card_click", { type: "dc", location: where })} data-testid={`${where}-dc-register-button`} className="btn-lime">
      DC residents: self-certify <ArrowUpRight className="h-4 w-4" />
    </a>
    <a href={BUSINESS.nonDcCardUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("medical_card_click", { type: "non_dc", location: where })} data-testid={`${where}-non-dc-register-button`} className={dark ? "btn-outline-light" : "btn-outline"}>
      Visitors: non-DC form <ArrowUpRight className="h-4 w-4" />
    </a>
  </div>
);

export default function MedicalCard() {
  return (
    <>
      <Seo {...PAGES.card} path="/medical-card" crumbs={CRUMBS} />
      <PageHeader crumbs={CRUMBS} eyebrow="Medical card registration" title="Self-certify for medical cannabis" highlight="in DC" image="/img/stairs-2.webp" lead="DC requires a medical cannabis registration before you can purchase. Adults 21+ can self-certify online in minutes through the District's official ABCA portal — no doctor's visit required.">
        <div className="mt-8"><CtaPair where="card-hero" dark /></div>
        <p className="mt-5 flex items-center gap-2 text-xs text-neutral-300"><ShieldCheck className="h-4 w-4 text-brand-lime" /> You'll be taken to myabca.dc.gov — the official Washington, DC government portal.</p>
      </PageHeader>

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-28" data-testid="card-steps">
        <Reveal className="mb-12 max-w-xl"><SectionHead eyebrow="The process" title="How self-certification works"><p className="mt-3 text-neutral-600">Four steps — most people finish in a few minutes.</p></SectionHead></Reveal>
        <ol className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {STEPS.map(([t, d], i) => (
            <Reveal as="li" key={t} delay={0.06 * i} className="h-full border border-neutral-200 bg-white p-8 transition-[border-color,box-shadow] duration-300 hover:border-brand-ink hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)]">
              <span className="font-display text-4xl font-black text-brand-lime [-webkit-text-stroke:1px_#121212]">0{i + 1}</span>
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight">{t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-500">{d}</p>
            </Reveal>
          ))}
        </ol>
      </section>

      <section className="bg-brand-sand py-20 sm:py-28" data-testid="card-audiences">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <Reveal className="mb-10"><SectionHead eyebrow="Residents & visitors" title="Who this is for" /></Reveal>
          <div className="grid gap-4 md:grid-cols-2">
            <Reveal className="rounded-2xl bg-brand-lime p-8 sm:p-10">
              <p className="text-xs font-bold uppercase tracking-[0.25em]">DC residents</p>
              <h3 className="mt-3 font-display text-3xl font-black tracking-tight">Self-certify online</h3>
              <p className="mt-3 text-sm leading-relaxed text-brand-ink/80">If you live in the District and you're 21+, register through the ABCA self-registration portal — done entirely online, no doctor's appointment.</p>
              <a href={BUSINESS.dcCardUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("medical_card_click", { type: "dc", location: "audience" })} data-testid="card-dc-register-link" className="btn-dark mt-6">Self-certify now <ArrowUpRight className="h-4 w-4" /></a>
            </Reveal>
            <Reveal delay={0.08} className="rounded-2xl bg-brand-ink p-8 text-white sm:p-10">
              <p className="eyebrow-lime">Out-of-town visitors</p>
              <h3 className="mt-3 font-display text-3xl font-black tracking-tight">Visiting DC?</h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-300">Register through ABCA's non-DC resident form, or bring a valid medical cannabis card from your home state.</p>
              <a href={BUSINESS.nonDcCardUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("medical_card_click", { type: "non_dc", location: "audience" })} data-testid="card-non-dc-register-link" className="btn-lime mt-6">Open the non-DC form <ArrowUpRight className="h-4 w-4" /></a>
            </Reveal>
          </div>
        </div>
      </section>

      <LicenseSection />

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-20 sm:px-6 sm:py-28 lg:grid-cols-5" data-testid="card-faq">
        <Reveal className="lg:col-span-2"><SectionHead eyebrow="Questions" title="Medical card FAQ" /></Reveal>
        <Reveal delay={0.1} className="lg:col-span-3"><FaqAccordion items={CARD_FAQS} /></Reveal>
      </section>

      <section className="on-dark grain relative bg-brand-ink text-white">
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="h-section">Ready to register?</h2>
          <p className="mt-3 max-w-xl text-neutral-300">It only takes a few minutes on the official DC government site — then you're ready to shop with us.</p>
          <div className="mt-8"><CtaPair where="card-footer" dark /></div>
          <p className="mt-6 text-sm text-neutral-400">Questions? Call <a href={BUSINESS.phoneHref} className="font-bold text-white underline underline-offset-4">{BUSINESS.phone}</a> or <Link to="/shop-all" className="font-bold text-brand-lime">browse the menu</Link>.</p>
        </div>
      </section>
    </>
  );
}
